/** Clients group (canonical `H6IHDfRP` — same Jobs hub as jobs.ts). The
 *  first-class Clients section: the full directory in one call (list), the
 *  per-client hub (detail), and standalone create. Edits reuse the existing
 *  client_update write in jobs.ts (jobsApi.updateClient). Goes through the
 *  shared client. */

import { api } from "./client";
import type {
  ClientsListResponse,
  ClientDetailResponse,
  ClientCreateInput,
  ClientCreateResponse,
  ClientArchiveResponse,
  ClientReopenResponse,
  ClientArchiveCandidatesResponse,
  PortalInviteResponse,
} from "@/types";

/** GET /clients/list — every client with per-client activity counts. No params;
 *  React does the search/filter/sort/pagination over the in-memory set. */
export function listClients(): Promise<ClientsListResponse> {
  return api.get<ClientsListResponse>("jobs", "clients/list");
}

/** GET /clients/detail?id= — the whole hub for one client: record + jobs (with
 *  is_lead) + open tasks + open applications + invoice financials. 404 if not
 *  found. */
export function getClientDetail(id: number): Promise<ClientDetailResponse> {
  return api.get<ClientDetailResponse>("jobs", "clients/detail", { query: { id } });
}

/** POST /clients/create — a standalone client (no job link). Server composes
 *  `address` from the split fields; no Xero push (xero_synced:false on create).
 *  Refetch the list on success. */
export function createClient(input: ClientCreateInput): Promise<ClientCreateResponse> {
  return api.post<ClientCreateResponse>("jobs", "clients/create", { body: input });
}

/** POST /clients/archive — set status=archived + archive the Xero contact
 *  (best-effort). 400 unless the client is eligible (every job archived, no open
 *  tasks/applications, nothing outstanding/overdue). Refetch detail on success;
 *  surface xero_archived=false softly (the local archive still succeeded). */
export function archiveClient(id: number): Promise<ClientArchiveResponse> {
  return api.post<ClientArchiveResponse>("jobs", "clients/archive", { body: { id } });
}

/** POST /clients/reopen — set status=active for an archived client. Unlike
 *  archive, this does NOT touch Xero: `has_xero_contact` tells the caller whether
 *  to remind the user to reactivate the Xero contact manually. Refetch detail. */
export function reopenClient(id: number): Promise<ClientReopenResponse> {
  return api.post<ClientReopenResponse>("jobs", "clients/reopen", { body: { id } });
}

/** GET /clients/archive_candidates — non-archived clients whose engagement is
 *  wound down (every job completed/archived), each with the archive-eligibility
 *  breakdown. Drives the Archive-clients workbench, which splits ready /
 *  not-ready client-side from the row's `ready` flag + counts. */
export function listArchiveCandidates(): Promise<ClientArchiveCandidatesResponse> {
  return api.get<ClientArchiveCandidatesResponse>("jobs", "clients/archive_candidates");
}

/** POST /portal/invite — grant this client access to the mobile app, and email
 *  them the download links. Idempotent, so the same call is also "resend".
 *
 *  The email deliberately carries NO sign-in link. A magic link lives 15 minutes
 *  and cannot survive an App Store detour, and one tapped before the app exists
 *  on the device has nothing to open — iOS falls through to the web page and
 *  strands the token there. The client installs the app, enters this email
 *  address, and requests their own link from inside it.
 *
 *  400 if the client has no email address, if the address already belongs to a
 *  different client, or if it's the App Store review account. Refetch detail on
 *  success — the `portal` block is what drives the button's label. */
export function invitePortalUser(clientId: number): Promise<PortalInviteResponse> {
  return api.post<PortalInviteResponse>("jobs", "portal/invite", { body: { client_id: clientId } });
}
